import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, X } from "lucide-react";
import { useTranslation } from "react-i18next";

type LastVariantDeleteModalProps = {
  open: boolean;
  pendingAction: "draft" | "product" | null;
  error: string | null;
  onClose: () => void;
  onMoveToDraftAndDelete: () => void;
  onDeleteProduct: () => void;
};

export function LastVariantDeleteModal({
  open,
  pendingAction,
  error,
  onClose,
  onMoveToDraftAndDelete,
  onDeleteProduct,
}: LastVariantDeleteModalProps) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  const busy = pendingAction !== null;

  useEffect(() => {
    if (!open) return;
    dialogRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busy) onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [busy, onClose, open]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-100 flex items-center justify-center bg-black/50 p-4"
      onMouseDown={event => {
        if (event.target === event.currentTarget && !busy) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="last-variant-delete-title"
        aria-describedby="last-variant-delete-description"
        tabIndex={-1}
        className="w-full max-w-lg overflow-hidden rounded-2xl border border-app-border bg-app-surface text-foreground shadow-2xl outline-none"
      >
        <div className="flex items-start justify-between gap-4 bg-brand px-5 py-4 text-white">
          <div className="flex items-center gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-white/15">
              <AlertTriangle size={22} aria-hidden="true" />
            </span>
            <h2 id="last-variant-delete-title" className="text-lg font-bold leading-tight">
              {t("variant.lastVariantDeleteTitle")}
            </h2>
          </div>
          <button
            type="button"
            aria-label={t("common.close")}
            disabled={busy}
            onClick={onClose}
            className="rounded-full p-1.5 transition-colors hover:bg-white/20 disabled:opacity-50 cursor-pointer"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-5 p-5">
          <p id="last-variant-delete-description" className="text-sm leading-6 text-app-muted">
            {t("variant.lastVariantDeleteDescription")}
          </p>

          {error && (
            <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300">
              {error}
            </p>
          )}

          <div className="grid grid-cols-[2fr_3fr] gap-3">
            <button
              type="button"
              disabled={busy}
              onClick={onDeleteProduct}
              className="min-h-12 rounded-xl bg-red-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
            >
              {pendingAction === "product"
                ? t("variant.deletingProduct")
                : t("variant.lastVariantDeleteWholeProduct")}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={onMoveToDraftAndDelete}
              className="min-h-12 rounded-xl border-2 border-brand bg-app-control px-3 py-2 text-sm font-semibold text-brand transition-colors hover:bg-brand/10 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
            >
              {pendingAction === "draft"
                ? t("variant.movingToDraftAndDeleting")
                : t("variant.moveToDraftAndDelete")}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
